import { ActionBulkModalAiLookalike } from '@aibees/elephant/components/action-bulk-modal-ai-lookalike';
import { AssignOwner } from '@aibees/elephant/components/assign-owner';
import { CreateActionBulkDialog } from '@aibees/elephant/components/create-action-bulk-dialog';
import { ListLabelActionBulkModal } from '@aibees/elephant/components/list-label-action-bulk-modal';
import { useLabelDialogConfig } from '@aibees/elephant/hooks/labels/use-label-dialog-config';
import { LabelType } from '@aibees/elephant/types/label';
import {
  DialogActions,
  DialogTitle,
  Form,
  FormOnSubmit,
} from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { Dialog, DialogContent, Fade } from '@mui/material';
import { memo, useCallback } from 'react';
import { config, scheme } from './config';
import type { AssignOwnerDialogProps as Props } from './types';
import { useAssignOwnerDialog } from './use-assign-owner-dialog';

const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) =>
  e.stopPropagation();
const DIALOG_PAPER_PROPS = { onMouseDown: stopPropagation };

let { defaultValues } = config;

export const AssignOwnerDialog = memo(({ className }: Props) => {
  // Custom hook for assign owner dialog logic
  const {
    ownerDialogState,
    ids,
    expandedAccordion,
    showAiLookalike,
    workspace,
    handleAssignOwner,
    handleClose,
    handleDialogClose,
    setExpandedAccordion,
    grouping,
  } = useAssignOwnerDialog();

  // Local state
  const loading = useBoolean(false);
  const createListDialogState = useBoolean(false);
  const createLabelDialogState = useBoolean(false);
  const {
    titleDialog: listTitleDialog,
    inputTitle: listInputTitle,
    placeholder: listPlaceholder,
  } = useLabelDialogConfig({ isLabel: false });
  const {
    titleDialog: labelTitleDialog,
    inputTitle: labelInputTitle,
    placeholder: labelPlaceholder,
  } = useLabelDialogConfig({ isLabel: true });
  const handleSubmit: FormOnSubmit = useCallback(
    (data) => {
      handleAssignOwner(data, {
        startProgress: loading.onTrue,
        endProgress: loading.onFalse,
        toggle: () => handleClose('owner'),
      });
    },
    [handleAssignOwner, handleClose, loading.onTrue, loading.onFalse]
  );

  defaultValues ||= {};

  return (
    <Form
      id="assign-owner-dialog"
      scheme={scheme}
      defaultValues={defaultValues}
      loading={loading.value}
      customSubmitButton
      onSubmit={handleSubmit}
    >
      <Dialog
        open={ownerDialogState.value}
        fullWidth={true}
        maxWidth="xs"
        TransitionComponent={Fade}
        onClose={handleDialogClose}
        PaperProps={DIALOG_PAPER_PROPS}
      >
        <DialogTitle
          title="Assign Owner"
          icon="account-tag"
          onClose={handleDialogClose}
        />

        <DialogContent padding="none">
          <ListLabelActionBulkModal
            type={LabelType.LIST}
            state={createListDialogState}
            firstState={ownerDialogState}
            optional={true}
            expandedAccordion={expandedAccordion}
            setExpandedAccordion={setExpandedAccordion}
          />

          <ListLabelActionBulkModal
            type={LabelType.LABEL}
            state={createLabelDialogState}
            firstState={ownerDialogState}
            optional={true}
            expandedAccordion={expandedAccordion}
            setExpandedAccordion={setExpandedAccordion}
          />

          {workspace && (
            <AssignOwner
              expandedAccordion={expandedAccordion}
              setExpandedAccordion={setExpandedAccordion}
            />
          )}

          <ActionBulkModalAiLookalike
            expandedAccordion={expandedAccordion}
            setExpandedAccordion={setExpandedAccordion}
          />
        </DialogContent>

        <DialogActions
          formId="assign-owner-dialog"
          submitTitle="Save"
          onClose={handleDialogClose}
        />
      </Dialog>

      <CreateActionBulkDialog
        state={createListDialogState}
        firstState={ownerDialogState}
        type={LabelType.LIST}
        title={listTitleDialog}
        inputTitle={listInputTitle}
        placeholder={listPlaceholder}
        grouping={grouping}
      />

      <CreateActionBulkDialog
        state={createLabelDialogState}
        firstState={ownerDialogState}
        type={LabelType.LABEL}
        title={labelTitleDialog}
        inputTitle={labelInputTitle}
        placeholder={labelPlaceholder}
        hasColor
        hasDescription
        grouping={grouping}
      />
    </Form>
  );
});

AssignOwnerDialog.displayName = 'AssignOwnerDialog';
